'use client';

import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { CollapsibleContentSliceDefaultPrimaryContentItem } from 'prismicio-types';
import { PrismicRichText } from '@prismicio/react';
import { useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import PrismicBodyText from 'src/components/PrismicBodyText/PrismicBodyText';
import { backgroundColourVariants } from 'src/constants/style';

/**
 * Props for `CollapsibleContent`.
 */
export type CollapsibleContentProps =
    SliceComponentProps<Content.CollapsibleContentSlice>;

/**
 * Component for "CollapsibleContent" Slices.
 */
const CollapsibleContent = ({
    slice,
}: CollapsibleContentProps): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <section
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            className="bg-white"
        >
            <div className={classNames('flex max-h-full w-screen flex-col')}>
                {/* Header */}
                <div
                    className={`${backgroundColourVariants[slice.primary.background_colour]} flex w-screen cursor-pointer flex-row items-center justify-center gap-2 p-4 text-black`}
                    onClick={toggleExpand}
                >
                    <h5 className="w-full max-w-[704px] text-center">
                        {slice.primary.title}
                    </h5>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={classNames(
                            'transition-transform duration-300',
                            {
                                'rotate-180 transform': isExpanded,
                            },
                        )}
                    />
                </div>
                {/* Collapsible Content */}
                <div
                    className={classNames(
                        'mx-auto mb-4 space-y-6 overflow-hidden px-4 transition-all duration-300 ease-in-out md:mb-16 md:w-[736px] md:px-0',
                        {
                            hidden: !isExpanded,
                            'max-h-max pt-8': isExpanded,
                        },
                    )}
                >
                    {slice.primary.content.map(
                        (
                            content: CollapsibleContentSliceDefaultPrimaryContentItem,
                            i: number,
                        ) => (
                            <div
                                key={i}
                                className="flex flex-col gap-6 bg-white"
                            >
                                {/* Content */}
                                <div className="flex flex-col gap-4 text-black">
                                    <PrismicRichText field={content.heading} />
                                    <PrismicBodyText content={content.body} />
                                </div>
                            </div>
                        ),
                    )}
                </div>
            </div>
        </section>
    );
};

export default CollapsibleContent;
